import { SEO } from "@/components/SEO";
import Navigation from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { LocalizedLink } from "@/components/LocalizedLink";
import { ArrowRight, MapPin, Globe, TrendingUp } from "lucide-react";

const EuropeanMarkets = () => {
  const markets = [
    {
      country: "Germany",
      flag: "🇩🇪",
      keyword: "Smart WC Germany",
      volume: "4,000-6,000",
      focus: "Technical Excellence & Engineering",
      features: ["Wassersparend (water-saving)", "Precision engineering", "Detailed specifications"],
      link: "/markets/germany"
    },
    {
      country: "United Kingdom",
      flag: "🇬🇧",
      keyword: "Intelligent Toilet UK",
      volume: "3,000-5,000",
      focus: "Quality & Heritage",
      features: ["Premium service", "British quality standards", "Comprehensive support"],
      link: "/markets/uk"
    },
    {
      country: "France",
      flag: "🇫🇷",
      keyword: "Toilette Intelligente France",
      volume: "2,500-4,000",
      focus: "Cultural Sophistication",
      features: ["Bidet culture integration", "Elegant design", "Cultural refinement"],
      link: "/markets/france"
    },
    {
      country: "Italy",
      flag: "🇮🇹",
      keyword: "WC Intelligent Italia",
      volume: "2,000-3,500",
      focus: "Design & Aesthetics",
      features: ["Italian design philosophy", "Visual excellence", "Luxury positioning"],
      link: "/markets/italy"
    },
    {
      country: "Spain",
      flag: "🇪🇸",
      keyword: "Sanitari Intelligenti",
      volume: "2,000-3,000",
      focus: "Mediterranean Elegance",
      features: ["Relationship-focused", "Regional variations", "Warm design approach"],
      link: "/markets/spain"
    }
  ];

  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "European Markets", url: "/european-markets" }
  ];

  return (
    <>
      <SEO
        title="Smart Toilet Europe — Premium Intelligent Sanitary Systems"
        description="Luxury smart toilet supply across Europe — UK, Germany, France, Italy and Spain. CE certified models, EU logistics and trade pricing for projects."
        keywords="smart toilet Europe, intelligent toilet UK, smart WC Germany, toilette intelligente France, WC intelligent Italia, sanitari intelligenti, European smart toilets, luxury bathroom technology Europe"
        breadcrumbs={breadcrumbs}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "European Smart Toilet Markets",
          "description": "Premium smart toilet solutions across European markets",
          "url": "https://www.aureum7trading.com/european-markets"
        }}
      />
      <Navigation />
      
      <main className="min-h-screen bg-gradient-to-b from-background to-background/95">
        {/* Hero Section */}
        <section className="pt-32 pb-20 px-4">
          <div className="container mx-auto max-w-6xl text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary mb-6">
              <Globe className="w-4 h-4" />
              <span className="text-sm font-medium">European Market Leader</span>
            </div>
            
            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
              Smart Toilet Europe
            </h1>
            
            <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              Premium intelligent sanitary systems engineered for European excellence. 
              Discover market-specific solutions across Germany, UK, France, Italy, and Spain.
            </p>

            <div className="flex flex-wrap justify-center gap-4 mb-12">
              <div className="flex items-center gap-2 px-6 py-3 bg-card rounded-lg border">
                <TrendingUp className="w-5 h-5 text-primary" />
                <span className="font-semibold">5,000-8,000</span>
                <span className="text-muted-foreground">monthly searches</span>
              </div>
              <div className="flex items-center gap-2 px-6 py-3 bg-card rounded-lg border">
                <MapPin className="w-5 h-5 text-primary" />
                <span className="font-semibold">5 Markets</span>
                <span className="text-muted-foreground">across Europe</span>
              </div>
            </div>
          </div>
        </section>

        {/* Markets Grid */}
        <section className="py-20 px-4 bg-card/30">
          <div className="container mx-auto max-w-6xl">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              European Market Solutions
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {markets.map((market) => (
                <Card key={market.country} className="p-6 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                  <div className="text-5xl mb-4">{market.flag}</div>
                  <h3 className="text-2xl font-bold mb-2">{market.country}</h3>
                  <p className="text-primary font-semibold mb-2">{market.keyword}</p>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                    <TrendingUp className="w-4 h-4" />
                    <span>{market.volume} searches/month</span>
                  </div>
                  
                  <div className="mb-4">
                    <p className="font-semibold text-sm text-primary mb-2">{market.focus}</p>
                    <ul className="space-y-1 text-sm text-muted-foreground">
                      {market.features.map((feature, idx) => (
                        <li key={idx}>• {feature}</li>
                      ))}
                    </ul>
                  </div>
                  
                  <Button asChild className="w-full group">
                    <LocalizedLink to={market.link}>
                      Explore {market.country}
                      <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </LocalizedLink>
                  </Button>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose European Smart Toilets */}
        <section className="py-20 px-4">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-3xl font-bold text-center mb-12">
              Why Choose European Smart Toilets?
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <h3 className="text-xl font-semibold">Engineering Excellence</h3>
                <p className="text-muted-foreground">
                  European-engineered intelligent toilet systems combine precision German engineering 
                  with Italian design aesthetics and French cultural sophistication.
                </p>
              </div>
              
              <div className="space-y-4">
                <h3 className="text-xl font-semibold">Market-Specific Solutions</h3>
                <p className="text-muted-foreground">
                  Each market receives tailored solutions: technical specifications for Germany, 
                  quality heritage for UK, cultural context for France, design focus for Italy.
                </p>
              </div>
              
              <div className="space-y-4">
                <h3 className="text-xl font-semibold">Premium Quality Standards</h3>
                <p className="text-muted-foreground">
                  All products meet strict European quality and safety standards, ensuring 
                  long-lasting performance and reliability across all markets.
                </p>
              </div>
              
              <div className="space-y-4">
                <h3 className="text-xl font-semibold">Comprehensive Support</h3>
                <p className="text-muted-foreground">
                  Multilingual customer support and technical assistance available across 
                  all European markets with local expertise and cultural understanding.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4 bg-gradient-to-r from-primary/10 to-accent/10">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Explore European Smart Toilet Solutions?
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Discover market-specific intelligent sanitary systems tailored for your region
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button asChild size="lg">
                <LocalizedLink to="/contact">Contact Our European Team</LocalizedLink>
              </Button>
              <Button asChild size="lg" variant="outline">
                <LocalizedLink to="/smart-toilets">View Product Collection</LocalizedLink>
              </Button>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </>
  );
};

export default EuropeanMarkets;